import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import topo from "./india-topo.json";

/**
 * India choropleth (state-level) built on react-simple-maps + bundled TopoJSON.
 * - Square viewBox so India fills the panel regardless of container height.
 * - In-map text labels showing STATE NAME + worker count rendered at each state's centroid.
 * - Floating tooltip on hover; click drills into the directory filtered by state.
 */

// Normalisation: TopoJSON has a few unusual spellings vs our DB
const ALIASES = {
  "arunanchal pradesh": "arunachal pradesh",
  "delhi": "delhi",
  "nct of delhi": "delhi",
  "national capital territory of delhi": "delhi",
  "andaman & nicobar island": "andaman & nicobar islands",
  "jammu & kashmir": "jammu and kashmir",
  "dadra and nagar haveli": "dadra & nagar haveli",
  "daman and diu": "daman & diu",
  "orissa": "odisha",
  "uttaranchal": "uttarakhand",
};

function norm(s) { return (s || "").trim().toLowerCase().replace(/\s+/g, " "); }
function canon(s) {
  const n = norm(s);
  return ALIASES[n] || n;
}

function fillFor(workers) {
  if (!workers || workers <= 0) return "#143570";
  if (workers >= 500) return "#ef4444"; // red — high
  if (workers >= 100) return "#f59e0b"; // amber — medium
  return "#F7B500";                     // gold — low
}

// Compact state name for in-map labels
function shortStateName(s) {
  if (!s) return "";
  return s
    .replace(/&/g, "&")
    .replace(/^Andaman .*$/i, "A & N")
    .replace(/^Dadra .*$/i, "DNH & DD")
    .replace(/^Jammu .*$/i, "J & K");
}

export default function IndiaChoropleth({ byState = [], height = 480, segment }) {
  const [hover, setHover] = useState(null);
  const navigate = useNavigate();
  const lookup = useMemo(() => {
    const m = {};
    byState.forEach((s) => { m[canon(s.state)] = s.workers || 0; });
    return m;
  }, [byState]);

  // On mobile the container is narrow, so use a square aspect so the map fills
  // its width (no dead navy band below). On md+ we honour the fixed `height` prop.
  return (
    <div
      className="relative w-full aspect-square md:aspect-auto"
      data-testid="india-choropleth"
      style={{ ['--india-md-h']: `${height}px` }}
    >
      <style>{`
        @media (min-width: 768px){[data-testid="india-choropleth"]{height:var(--india-md-h);}}
        @keyframes india-state-pulse {
          0%, 100% { filter: drop-shadow(0 0 0px rgba(247,181,0,0.55)) brightness(1); }
          50%      { filter: drop-shadow(0 0 14px rgba(247,181,0,0.95)) brightness(1.12); }
        }
        [data-testid="india-choropleth"] .ic-state-active { animation: india-state-pulse 2.4s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
      `}</style>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 1650, center: [82, 23] }}
        width={780}
        height={880}
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <Geographies geography={topo}>
          {({ geographies }) => (
            <>
              {/* 1. Filled state shapes */}
              {geographies.map((geo) => {
                const name = geo.properties?.name || "";
                const cn = canon(name);
                const workers = lookup[cn] || 0;
                const fill = fillFor(workers);
                const isActive = workers > 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#0b1426"
                    strokeWidth={0.4}
                    className={isActive ? "ic-state-active" : undefined}
                    onMouseEnter={(e) => setHover({ name, workers, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHover({ name, workers, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => {
                      if (!name) return;
                      const params = new URLSearchParams();
                      params.set("state", name);
                      if (segment) params.set("segment", segment);
                      navigate(`/directory?${params.toString()}`);
                    }}
                    style={{
                      default: { outline: "none", transition: "all 0.15s" },
                      hover: { fill: "#fde68a", cursor: "pointer", outline: "none" },
                      pressed: { fill: "#fbbf24", outline: "none" },
                    }}
                  />
                );
              })}

              {/* 2. In-map labels for every state with workers > 0 */}
              {geographies
                .filter((geo) => (lookup[canon(geo.properties?.name)] || 0) > 0)
                .map((geo) => {
                  const name = geo.properties?.name || "";
                  const workers = lookup[canon(name)] || 0;
                  let [lon, lat] = geoCentroid(geo);
                  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
                  return (
                    <Marker key={`lbl-${geo.rsmKey}`} coordinates={[lon, lat]}>
                      <g style={{ pointerEvents: "none" }}>
                        <text
                          textAnchor="middle"
                          y={-2}
                          style={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontWeight: 700,
                            fontSize: 12,
                            fill: "#ffffff",
                            paintOrder: "stroke",
                            stroke: "#02152E",
                            strokeWidth: 3.5,
                            letterSpacing: 0.3,
                          }}
                        >
                          {shortStateName(name).toUpperCase()}
                        </text>
                        <text
                          textAnchor="middle"
                          y={13}
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 900,
                            fontSize: 13,
                            fill: "#F7B500",
                            paintOrder: "stroke",
                            stroke: "#02152E",
                            strokeWidth: 3.5,
                          }}
                        >
                          {workers}
                        </text>
                      </g>
                    </Marker>
                  );
                })}
            </>
          )}
        </Geographies>
      </ComposableMap>

      {hover && (
        <div
          className="pointer-events-none fixed z-50 rounded border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-xs text-white shadow-lg"
          style={{ top: hover.y + 12, left: hover.x + 12 }}
        >
          <div className="font-heading text-sm font-bold text-ncc-gold">{hover.name}</div>
          <div>{hover.workers > 0 ? `${hover.workers} workers` : "No data"}</div>
          <div className="mt-0.5 text-[10px] text-cyan-300">▸ Click to view directory</div>
        </div>
      )}
    </div>
  );
}
